import {
    NeighborhoodJob,
    NeighborhoodServiceAreaInfo,
} from 'rwl-shared/dist/models/neighborhood';
import { PlaceInfo } from 'rwl-shared/dist/models/ServiceArea';
import {
    V2DensityJob,
    V2NeighborhoodServiceArea,
    V2PlaceInfo,
    V2TopJob,
} from './v2NeighborhoodTypes';

/**
 * Adapters from the v2 widget service's neighborhood shapes to the shapes this
 * widget already consumes, so the migration is confined to the fetchers and no
 * component changes.
 *
 * Scope note: only the *job* side is adapted, and the anonymized `densityJobs`
 * pins are folded into the same `jobs` array (see `adaptV2ServiceArea`).
 * `NeighborhoodServiceAreaInfo` has no `reviews` field — service-area reviews
 * reach the UI through the featured content endpoint, which has no v2 counterpart
 * yet. The v2 payload does carry `reviews`, plus `description`/`additionalMedia`
 * per job, which is what would later let the by-id job/review fetchers be deleted
 * rather than ported.
 */

/**
 * Rebuild the legacy `geometry` nesting the widget reads.
 *
 * v2 serves `geometry` for this widget's benefit but types it optional, while
 * `location`/`viewport` are always present. Deriving the nesting from the
 * flattened fields when it is absent keeps map centering, clustering, and bounds
 * fitting working either way — all three read `placeInfo.geometry`, so trusting
 * the optional field alone would silently flatten the map to its default view.
 */
const adaptPlaceInfo = (placeInfo: V2PlaceInfo): PlaceInfo => {
    const geometry = placeInfo.geometry ?? {
        location: placeInfo.location,
        viewport: placeInfo.viewport,
    };

    return {
        geometry,
        // v2 carries no `name` (the legacy value came from Google Places), but
        // the shared type requires it. Nothing in this widget reads it — every
        // placeInfo access is `place_id` or `geometry` — so use the closest thing
        // v2 does serve and fall back to empty rather than inventing a value.
        name: placeInfo.formatted_address ?? '',
        place_id: placeInfo.place_id,
        ...(placeInfo.types ? { types: placeInfo.types } : {}),
        ...(placeInfo.plus_code ? { plus_code: placeInfo.plus_code } : {}),
    };
};

export const adaptV2TopJob = (job: V2TopJob): NeighborhoodJob => ({
    id: job.id,
    published: job.published,
    // v2 omits geolocation for jobs that never resolved to a coordinate. The
    // declared type requires it, but v1's serializer emitted
    // `{lat: undefined, lng: undefined}` for those same jobs, and
    // NeighborhoodMarkers already skips any job with a falsy lat/lng. Carrying
    // the gap through therefore reproduces v1 exactly; substituting 0/0 would
    // instead plant a pin in the Gulf of Guinea.
    geolocation: {
        lat: job.geolocation?.lat,
        lng: job.geolocation?.lng,
    } as NeighborhoodJob['geolocation'],
    coverImageUrl: job.coverImageUrl,
    ...(job.title !== undefined ? { title: job.title } : {}),
    ...(job.reviewId !== undefined ? { reviewId: job.reviewId } : {}),
});

/**
 * Adapt an anonymized density pin (a non-published worksite: id + coordinate) to
 * the job shape the map renders. `published: false` selects `UnpublishedMarker` —
 * an 8px dot whose popover reads only "Completed project" — and the empty
 * `coverImageUrl` reflects that no media is exposed. A missing coordinate is
 * carried through as `{lat: undefined, lng: undefined}` (the same as
 * `adaptV2TopJob`), which `NeighborhoodMarkers` skips rather than planting a pin
 * at 0/0.
 */
export const adaptV2DensityJob = (job: V2DensityJob): NeighborhoodJob => ({
    id: job.id,
    published: false,
    geolocation: {
        lat: job.geolocation?.lat,
        lng: job.geolocation?.lng,
    } as NeighborhoodJob['geolocation'],
    coverImageUrl: '',
});

export const adaptV2ServiceArea = (
    area: V2NeighborhoodServiceArea
): NeighborhoodServiceAreaInfo => ({
    neighborhood: area.neighborhood,
    postcode: area.postcode,
    // All-status count, matching v1's `jobs.count` — the number the map displays
    // and what clusterServiceAreas/filterServiceAreas weight by. Falls back to
    // the published-only `jobCount` for a response that predates the density work
    // (RFC-NEIGHBORHOOD-MAP-DENSITY); see v2NeighborhoodTypes for why it can be
    // absent.
    jobCount: area.jobCountTotal ?? area.jobCount,
    reviewCount: area.reviewCount,
    averageRating: area.averageRating,
    ...(area.placeInfo ? { placeInfo: adaptPlaceInfo(area.placeInfo) } : {}),
    // Published jobs plus the anonymized density pins, reproducing v1's map: v1
    // read the raw unfiltered `ServiceArea.jobs.list` and rendered non-published
    // jobs as `UnpublishedMarker` dots. Density pins are appended after the
    // published jobs; order does not matter to the markers.
    jobs: [
        ...area.jobs.map(adaptV2TopJob),
        ...(area.densityJobs ?? []).map(adaptV2DensityJob),
    ],
});

export const adaptV2ServiceAreas = (
    areas: V2NeighborhoodServiceArea[]
): NeighborhoodServiceAreaInfo[] => areas.map(adaptV2ServiceArea);
